/**
 * Device Manager Service
 * Handles device-specific operations and Android integration
 */
class DeviceManager {
    constructor() {
        this.deviceId = null;
        this.isAndroidDevice = false;
        this.deviceInfo = null;
        this.androidInterface = null;
        this.listeners = [];
        
        this.init();
    }
    
    init() {
        // Check for Android WebView interface
        if (window.AndroidDevice) {
            this.isAndroidDevice = true;
            this.androidInterface = window.AndroidDevice;
            this.deviceId = this.androidInterface.getDeviceId();
            
            console.log('DeviceManager: Running in Android device mode', {
                deviceId: this.deviceId,
                hasInterface: !!this.androidInterface
            });
            
            // Load device info
            this.loadDeviceInfo();
            
            // Set up device event listeners
            this.setupDeviceEventListeners();
        } else {
            // Development mode: Force Android device behavior on localhost
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                this.isAndroidDevice = true;
                this.deviceId = 'dev-device-' + Date.now();
                console.log('DeviceManager: Running in development Android device mode', {
                    deviceId: this.deviceId,
                    hostname: window.location.hostname
                });
            } else {
                console.log('DeviceManager: Running in browser mode');
            }
        }
        
        // Listen for device ready event from Android WebView
        window.addEventListener('tabblyDeviceReady', (event) => {
            console.log('DeviceManager: Device ready event received', event.detail);
            this.handleDeviceReady(event.detail);
        });
        
        // Check localStorage for device context
        this.loadFromLocalStorage();
    }
    
    loadFromLocalStorage() {
        try {
            // Load device ID from localStorage (injected by Android WebView)
            const storedDeviceId = localStorage.getItem('device_id');
            if (storedDeviceId && !this.deviceId) {
                this.deviceId = storedDeviceId;
                console.log('DeviceManager: Device ID loaded from localStorage:', this.deviceId);
            }
            
            // Load device flag
            const isAndroid = localStorage.getItem('is_android_device') === 'true';
            if (isAndroid && !this.isAndroidDevice) {
                this.isAndroidDevice = true;
                console.log('DeviceManager: Android flag loaded from localStorage');
            }
            
            // Load device branding
            const brandingData = localStorage.getItem('restaurant_branding');
            if (brandingData) {
                try {
                    this.restaurantBranding = JSON.parse(brandingData);
                    console.log('DeviceManager: Restaurant branding loaded from localStorage');
                } catch (e) {
                    console.warn('DeviceManager: Failed to parse restaurant branding:', e);
                }
            }
        } catch (error) {
            console.warn('DeviceManager: Error loading from localStorage:', error);
        }
    }
    
    loadDeviceInfo() {
        if (this.androidInterface && this.androidInterface.getDeviceInfo) {
            try {
                const deviceInfoStr = this.androidInterface.getDeviceInfo();
                if (deviceInfoStr && deviceInfoStr !== 'Loading...') {
                    this.deviceInfo = JSON.parse(deviceInfoStr);
                    console.log('DeviceManager: Device info loaded:', this.deviceInfo);
                    
                    // Notify listeners
                    this.notifyListeners('deviceInfoLoaded', this.deviceInfo);
                }
            } catch (error) {
                console.warn('DeviceManager: Failed to load device info:', error);
            }
        }
    }
    
    setupDeviceEventListeners() {
        // Set up periodic heartbeat
        if (this.isAndroidDevice) {
            // Send heartbeat every 5 minutes
            setInterval(() => {
                this.triggerHeartbeat();
            }, 5 * 60 * 1000);
        }
    }
    
    handleDeviceReady(detail) {
        this.deviceId = detail.deviceId || this.deviceId;
        this.isAndroidDevice = detail.isAndroid || this.isAndroidDevice;
        
        console.log('DeviceManager: Device context updated:', {
            deviceId: this.deviceId,
            isAndroid: this.isAndroidDevice
        });
        
        // Notify listeners
        this.notifyListeners('deviceReady', {
            deviceId: this.deviceId,
            isAndroid: this.isAndroidDevice
        });
    }
    
    // Device Operations
    
    triggerHeartbeat() {
        if (this.androidInterface && this.androidInterface.triggerHeartbeat) {
            try {
                const success = this.androidInterface.triggerHeartbeat();
                console.log('DeviceManager: Heartbeat triggered:', success);
                return success;
            } catch (error) {
                console.warn('DeviceManager: Failed to trigger heartbeat:', error);
                return false;
            }
        }
        return false;
    }
    
    exitKioskMode(pin) {
        if (this.androidInterface && this.androidInterface.exitKioskMode) {
            try {
                const success = this.androidInterface.exitKioskMode(pin);
                console.log('DeviceManager: Exit kiosk mode requested:', success);
                return success;
            } catch (error) {
                console.warn('DeviceManager: Failed to exit kiosk mode:', error);
                return false;
            }
        }
        return false;
    }
    
    logEvent(event, data = null) {
        if (this.androidInterface && this.androidInterface.logEvent) {
            try {
                const success = this.androidInterface.logEvent(event, data ? JSON.stringify(data) : null);
                return success;
            } catch (error) {
                console.warn('DeviceManager: Failed to log event:', error);
                return false;
            }
        }
        
        // Fallback to console logging\n        console.log('DeviceManager Event:', event, data);
        return true;
    }
    
    // Getters
    
    getDeviceId() {
        return this.deviceId;
    }
    
    isAndroid() {
        return this.isAndroidDevice;
    }
    
    getDeviceInfo() {
        return this.deviceInfo;
    }
    
    getRestaurantBranding() {
        return this.restaurantBranding;
    }
    
    // Event System
    
    addListener(callback) {
        if (typeof callback === 'function') {
            this.listeners.push(callback);
        }
    }
    
    removeListener(callback) {
        const index = this.listeners.indexOf(callback);
        if (index > -1) {
            this.listeners.splice(index, 1);
        }
    }
    
    notifyListeners(event, data) {
        this.listeners.forEach(callback => {
            try {
                callback(event, data);
            } catch (error) {
                console.warn('DeviceManager: Listener error:', error);
            }
        });
    }
    
    // Admin Operations
    
    showAdminDialog() {
        // Create admin dialog for device operations
        const dialog = this.createAdminDialog();
        document.body.appendChild(dialog);
        
        // Focus on PIN input
        const pinInput = dialog.querySelector('#admin-pin-input');
        if (pinInput) {
            pinInput.focus();
        }
    }
    
    createAdminDialog() {
        const dialog = document.createElement('div');
        dialog.className = 'device-admin-dialog';
        dialog.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
        `;
        
        dialog.innerHTML = `
            <div style="
                background: white;
                padding: 2rem;
                border-radius: 8px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                max-width: 400px;
                width: 90%;
            ">
                <h3 style="margin: 0 0 1rem 0; color: #030640;">Device Administration</h3>
                <p style="margin: 0 0 1rem 0; color: #666;">Enter PIN to access device settings:</p>
                <input 
                    type="password" 
                    id="admin-pin-input" 
                    placeholder="Enter PIN"
                    style="
                        width: 100%;
                        padding: 0.75rem;
                        border: 2px solid #ddd;
                        border-radius: 4px;
                        font-size: 1.2rem;
                        text-align: center;
                        margin-bottom: 1rem;
                        box-sizing: border-box;
                    "
                    maxlength="4"
                />
                <div style="display: flex; gap: 0.5rem;">
                    <button id="admin-exit-btn" style="
                        flex: 1;
                        padding: 0.75rem;
                        background: #030640;
                        color: white;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 1rem;
                    ">Exit Kiosk</button>
                    <button id="admin-heartbeat-btn" style="
                        flex: 1;
                        padding: 0.75rem;
                        background: #C7D941;
                        color: #030640;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 1rem;
                    ">Heartbeat</button>
                    <button id="admin-cancel-btn" style="
                        flex: 1;
                        padding: 0.75rem;
                        background: #ccc;
                        color: #333;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 1rem;
                    ">Cancel</button>
                </div>
            </div>
        `;
        
        // Event listeners
        const pinInput = dialog.querySelector('#admin-pin-input');
        const exitBtn = dialog.querySelector('#admin-exit-btn');
        const heartbeatBtn = dialog.querySelector('#admin-heartbeat-btn');
        const cancelBtn = dialog.querySelector('#admin-cancel-btn');
        
        const closeDialog = () => {
            dialog.remove();
        };
        
        exitBtn.addEventListener('click', () => {
            const pin = pinInput.value;
            if (this.exitKioskMode(pin)) {
                alert('Exiting kiosk mode...');
                closeDialog();
            } else {
                alert('Invalid PIN');
                pinInput.value = '';
                pinInput.focus();
            }
        });
        
        heartbeatBtn.addEventListener('click', () => {
            if (this.triggerHeartbeat()) {
                alert('Heartbeat sent successfully');
            } else {
                alert('Failed to send heartbeat');
            }
        });
        
        cancelBtn.addEventListener('click', closeDialog);
        
        // Close on background click
        dialog.addEventListener('click', (e) => {
            if (e.target === dialog) {
                closeDialog();
            }
        });
        
        // Enter key to submit
        pinInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                exitBtn.click();
            }
        });
        
        return dialog;
    }
}

// Create global instance
const deviceManager = new DeviceManager();

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
    module.exports = DeviceManager;
}

// Global access
window.TabblyDeviceManager = deviceManager;

export { DeviceManager };
export default deviceManager;